import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  message,
  Descriptions,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tag,
  Spin,
} from "antd";
import {
  ApiOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DisconnectOutlined,
  LinkOutlined,
  ReloadOutlined,
  ImportOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { Company } from "../types/company";
import {
  PicqerService,
  PicqerFulfilmentCustomer,
} from "../services/picqerService";
import { PicqerProductImport } from "./PicqerProductImport";

const { Text } = Typography;
const { Option } = Select;

interface AdminPicqerIntegrationSectionProps {
  company: Company;
  onRefresh: () => void;
}

const AdminPicqerIntegrationSection: React.FC<
  AdminPicqerIntegrationSectionProps
> = ({ company, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [apiKeyName, setApiKeyName] = useState("");
  const [form] = Form.useForm();

  const [fulfilmentCustomers, setFulfilmentCustomers] = useState<
    PicqerFulfilmentCustomer[]
  >([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Check if company is connected to Picqer
  const isConnected = company.picqer_enabled && company.picqer_api_key;
  const hasFullIntegration =
    isConnected && company.picqer_fulfilment_customer_id;

  useEffect(() => {
    if (showSetupModal) {
      loadFulfilmentCustomers();
    }
  }, [showSetupModal]);

  const loadFulfilmentCustomers = async () => {
    setLoadingCustomers(true);
    try {
      console.log("Loading fulfillment customers using general API key...");

      const result = await PicqerService.getFulfilmentCustomers();

      console.log("Fulfillment customers result:", result);

      if (result.success && result.data) {
        console.log(
          "Successfully loaded fulfillment customers:",
          result.data.length
        );
        setFulfilmentCustomers(result.data);
      } else {
        console.error("Failed to load fulfillment customers:", result.error);
        message.error(
          `Failed to load fulfillment customers: ${
            result.error || "Unknown error"
          }`
        );
        setFulfilmentCustomers([]);
      }
    } catch (error) {
      console.error("Error loading fulfillment customers:", error);
      message.error(
        "Failed to load fulfillment customers - please check console for details"
      );
      setFulfilmentCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleTestConnection = async () => {
    if (!company.picqer_api_key) {
      message.error("No API key configured");
      return;
    }

    setTestingConnection(true);
    try {
      const result = await PicqerService.testConnection(company.picqer_api_key);

      if (result.success) {
        message.success("Picqer connection test successful!");
      } else {
        message.error(`Connection test failed: ${result.error}`);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      message.error("Connection test failed");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSetupIntegration = () => {
    if (company.picqer_api_key) {
      // If API key exists, show fulfillment customer selection but don't populate the API key field
      form.setFieldsValue({
        apiKey: "", // Don't show the actual API key for security
        fulfilmentCustomerId: company.picqer_fulfilment_customer_id,
      });
      setShowSetupModal(true);
    } else {
      // If no API key, show setup form
      form.resetFields();
      setShowSetupModal(true);
    }
  };

  const handleSaveIntegration = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const result = await PicqerService.updateCompanyPicqerConnection(
        company.id!,
        {
          apiKey: values.apiKey,
          fulfilmentCustomerId: values.fulfilmentCustomerId || 0,
          enabled: true,
        }
      );

      if (result.success) {
        message.success("Picqer integration updated successfully!");
        setShowSetupModal(false);
        onRefresh();
      } else {
        message.error(`Failed to update integration: ${result.error}`);
      }
    } catch (error) {
      console.error("Error saving integration:", error);
      message.error("Failed to save integration settings");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: "Disconnect Picqer Integration",
      content:
        "Are you sure you want to disconnect this company from Picqer? This will disable purchase order creation and other Picqer features.",
      okText: "Disconnect",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const result = await PicqerService.disconnectCompanyPicqer(
            company.id!
          );

          if (result.success) {
            message.success("Picqer integration disconnected");
            onRefresh();
          } else {
            message.error(`Failed to disconnect: ${result.error}`);
          }
        } catch (error) {
          console.error("Error disconnecting:", error);
          message.error("Failed to disconnect integration");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const showApiKeyModal = () => {
    const keyName = `Dashboard - ${company.name || "Company"}`;
    setApiKeyName(keyName);
    setApiKeyModalVisible(true);
  };

  const handleGoToPicqer = () => {
    window.open(
      "https://e-freight-forwarding.picqer.com/config/apikeys/create",
      "_blank"
    );
    setApiKeyModalVisible(false);
  };

  const handleCopyKeyName = () => {
    navigator.clipboard.writeText(apiKeyName);
    message.success("API key name copied to clipboard!");
  };

  return (
    <div>
      {/* Integration Status Alert */}
      {isConnected ? (
        hasFullIntegration ? (
          <Alert
            message="Picqer Integration Active"
            description="This company is fully connected to Picqer with fulfillment customer configured. Purchase orders and product sync are available."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            message="Picqer Partially Connected"
            description="API key is configured but no fulfillment customer is selected. Some features may not work properly."
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )
      ) : (
        <Alert
          message="Picqer Not Connected"
          description="This company is not connected to Picqer. Set up the integration to enable purchase orders and inventory management."
          type="info"
          showIcon
          icon={<ApiOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <ApiOutlined />
            Picqer Integration Settings
            {isConnected && <Tag color="green">Connected</Tag>}
          </Space>
        }
        extra={
          <Space>
            {isConnected && (
              <>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleTestConnection}
                  loading={testingConnection}
                >
                  Test Connection
                </Button>
                <Button
                  icon={<ImportOutlined />}
                  onClick={() => setShowImportModal(true)}
                >
                  Import/Link Products
                </Button>
              </>
            )}
            <Button
              type="primary"
              icon={isConnected ? <SettingOutlined /> : <LinkOutlined />}
              onClick={handleSetupIntegration}
            >
              {isConnected ? "Configure" : "Connect to Picqer"}
            </Button>
            {isConnected && (
              <Button
                danger
                icon={<DisconnectOutlined />}
                onClick={handleDisconnect}
                loading={loading}
              >
                Disconnect
              </Button>
            )}
          </Space>
        }
      >
        {isConnected ? (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="API Key" span={2}>
              <Text code>
                {company.picqer_api_key
                  ? `${company.picqer_api_key.substring(0, 8)}...`
                  : "Not configured"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color="green">Connected</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Enabled">
              {company.picqer_enabled ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="red">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Fulfillment Customer ID" span={2}>
              {company.picqer_fulfilment_customer_id ? (
                <Text strong>#{company.picqer_fulfilment_customer_id}</Text>
              ) : (
                <Text type="secondary">Not configured</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
          >
            <ApiOutlined
              style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
            />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              No Picqer Integration
            </div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>
              Connect this company to Picqer to enable purchase orders,
              inventory sync, and fulfillment features.
            </div>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={handleSetupIntegration}
            >
              Set Up Picqer Integration
            </Button>
          </div>
        )}
      </Card>

      {/* Setup/Configuration Modal */}
      <Modal
        title={
          isConnected
            ? "Configure Picqer Integration"
            : "Set Up Picqer Integration"
        }
        open={showSetupModal}
        onCancel={() => setShowSetupModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowSetupModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={handleSaveIntegration}
          >
            {isConnected ? "Update Integration" : "Connect to Picqer"}
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="apiKey"
            label="Picqer API Key"
            rules={[
              { required: true, message: "Please enter your Picqer API key" },
              { min: 10, message: "API key should be at least 10 characters" },
            ]}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input.Password
                placeholder="Enter your Picqer API key"
                autoComplete="off"
                visibilityToggle={{ visible: false }}
                defaultValue=""
              />
              <Button
                type="link"
                icon={<KeyOutlined />}
                onClick={showApiKeyModal}
                style={{ padding: 0, height: "auto" }}
              >
                Create new API key
              </Button>
            </Space>
          </Form.Item>

          <Form.Item
            name="fulfilmentCustomerId"
            label="Fulfillment Customer"
            help="Select the fulfillment customer for this company in Picqer"
          >
            <Select
              placeholder="Select fulfillment customer"
              loading={loadingCustomers}
              showSearch
              optionFilterProp="children"
              onFocus={() => {
                if (fulfilmentCustomers.length === 0) {
                  loadFulfilmentCustomers();
                }
              }}
            >
              {fulfilmentCustomers
                .filter((customer) => customer.idfulfilment_customer)
                .map((customer) => (
                  <Option
                    key={customer.idfulfilment_customer}
                    value={customer.idfulfilment_customer}
                  >
                    <Space>
                      <Text key="name" strong>
                        {customer.name}
                      </Text>
                      <Text key="id" type="secondary">
                        #{customer.idfulfilment_customer}
                      </Text>
                      {customer.active && (
                        <Tag key="status" color="green">
                          Active
                        </Tag>
                      )}
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Product Import Modal */}
      <PicqerProductImport
        visible={showImportModal}
        onCancel={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          onRefresh();
        }}
        company={company}
      />

      {/* API Key Creation Modal */}
      <Modal
        title="Create New API Key"
        open={apiKeyModalVisible}
        onCancel={() => setApiKeyModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setApiKeyModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="copy" onClick={handleCopyKeyName}>
            Copy Key Name
          </Button>,
          <Button key="picqer" type="primary" onClick={handleGoToPicqer}>
            Go to Picqer
          </Button>,
        ]}
      >
        <div>
          <p>Use this name for your new API key:</p>
          <Input value={apiKeyName} readOnly style={{ marginBottom: 16 }} />
          <p style={{ fontSize: "12px", color: "#666" }}>
            Click "Copy Key Name" to copy the name, then click "Go to Picqer" to
            create the API key.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPicqerIntegrationSection;
